import { ReactNode, useEffect, useMemo, useState } from 'react'

import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'

import useCartStore from '../../../store/cartStore'
import { CartItem } from '../../../types'

import UnavailableItemsResolver from './UnavailableItemsResolver'

import styles from './SubstitutesByDeliveryMethodModal.module.scss'

interface BasicModalReplacementsByDeliveryMethodProps {
  trigger?: ReactNode
  modalOpenTrigger?: number
  deliveryMethodId: string | null
}

interface ModalReplacementsByDeliveryMethodWithCustomProductsProps
  extends BasicModalReplacementsByDeliveryMethodProps {
  customProducts: true
  productsNotAvailable: CartItem[] | null
}

interface ModalReplacementsByDeliveryMethodWithoutCustomProductsProps
  extends BasicModalReplacementsByDeliveryMethodProps {
  customProducts: false
  productsNotAvailable?: never
}

const SubstitutesByDeliveryMethodModal = ({
  trigger,
  modalOpenTrigger = 0,
  deliveryMethodId,
  customProducts = false,
  productsNotAvailable
}:
  | ModalReplacementsByDeliveryMethodWithCustomProductsProps
  | ModalReplacementsByDeliveryMethodWithoutCustomProductsProps) => {
  const [open, setOpen] = useState<boolean>(false)
  const { cart, productsNotAvailableForDeliveryMethod } = useCartStore()

  useEffect(() => {
    modalOpenTrigger > 0 && setOpen(true)
  }, [modalOpenTrigger])

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  const providedProductsNotAvailable = useMemo(
    () =>
      customProducts
        ? productsNotAvailable
        : productsNotAvailableForDeliveryMethod,
    [
      customProducts,
      productsNotAvailable,
      productsNotAvailableForDeliveryMethod
    ]
  )

  const deliveryName = cart?.deliverySlot?.deliveryMethod?.name

  return (
    <>
      <div className={styles.triggerWrapper} onClick={handleOpen}>
        {trigger}
      </div>
      <Modal
        id='product-replacements-by-delivery-method-modal'
        onClose={handleClose}
        open={
          open &&
          !!providedProductsNotAvailable &&
          providedProductsNotAvailable.length > 0
        }
        size='large'
      >
        <ModalTitle>Zamienniki dla metody dostawy</ModalTitle>
        <ModalDescription>
          Przykro nam, ale niektóre produkty w Twoim koszyku nie są obecnie
          dostępne dla wybranej metody dostawy
          {deliveryName ? `(${deliveryName})` : ''}. Wymień niedostępne produkty
          aby otrzymać zamówienie w aktualnie wybranym terminie.
        </ModalDescription>
        <div className={styles.modalContent}>
          <div className={styles.title}>
            Wymień lub usuń produkt (1 z{' '}
            {providedProductsNotAvailable?.length || 0})
          </div>
          <hr className={styles.hr} />
          {open && providedProductsNotAvailable && (
            <UnavailableItemsResolver
              handleClose={handleClose}
              productsNotAvailable={providedProductsNotAvailable || []}
              deliveryMethodId={deliveryMethodId}
            />
          )}
        </div>
      </Modal>
    </>
  )
}

export default SubstitutesByDeliveryMethodModal
